import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, STATI, MAGAZZINI, STAGIONI, SCAFFALI } from "@/lib/api";
import { Plus, Search, Filter, Trash2, Pencil, X, MessageCircle, FileSpreadsheet, Printer, FileText } from "lucide-react";
import EuropeanLicensePlate from "@/components/EuropeanLicensePlate";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function Depositi() {
  const [sp, setSp] = useSearchParams();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const filters = useMemo(() => ({
    search: sp.get("search") || "",
    stato: sp.get("stato") || "",
    magazzino: sp.get("magazzino") || "",
    stagionalita: sp.get("stagionalita") || "",
    scaffale: sp.get("scaffale") || "",
    settore: sp.get("settore") || "",
  }), [sp]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next, { replace: true });
  };

  const clearFilters = () => setSp(new URLSearchParams(), { replace: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/deposits", { params });
      setDeposits(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const changeStato = async (d, stato) => {
    try {
      await api.patch(`/deposits/${d.id}/stato`, { stato });
      toast.success(`Stato aggiornato: ${stato}`);
      load();
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  const sendWhatsApp = async (d) => {
    try {
      await api.post(`/whatsapp/send-reminder/${d.id}`);
      toast.success("Promemoria WhatsApp inviato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio WhatsApp");
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Eliminare il deposito?")) return;
    try {
      await api.delete(`/deposits/${d.id}`);
      toast.success("Deposito eliminato");
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="depositi-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Inventario</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
            Depositi
          </h1>
        </div>
        <Link
          to="/depositi/nuovo"
          data-testid="new-deposit-button"
          className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black transition-colors inline-flex items-center gap-2 self-start"
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          Nuovo Deposito
        </Link>
      </div>

      {/* Export bar */}
      <div className="flex flex-wrap gap-2 print:hidden">
        <button
          onClick={() => {
            const pin = localStorage.getItem("app_pin") || "";
            const params = new URLSearchParams();
            if (filters.stato) params.set("stato", filters.stato);
            if (filters.magazzino) params.set("magazzino", filters.magazzino);
            const url = `${API}/exports/deposits.xlsx?${params.toString()}`;
            fetch(url, { headers: { "X-App-Pin": pin } })
              .then((r) => r.blob())
              .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `gomme-track_depositi_${new Date().toISOString().slice(0,10)}.xlsx`;
                a.click();
                URL.revokeObjectURL(a.href);
              })
              .catch(() => toast.error("Errore download Excel"));
          }}
          data-testid="export-excel-btn"
          className="border-2 border-black bg-white text-black px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-2"
        >
          <FileSpreadsheet className="w-4 h-4" strokeWidth={3} />
          Excel
        </button>
        <button
          onClick={() => window.print()}
          data-testid="print-btn"
          className="border-2 border-black bg-white text-black px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-2"
        >
          <Printer className="w-4 h-4" strokeWidth={3} />
          Stampa / PDF
        </button>
      </div>

      {/* Search + filters */}
      <div className="border-2 border-black bg-white p-4 space-y-4">
        <div className="flex items-center border-2 border-black bg-white">
          <Search className="w-5 h-5 ml-3" strokeWidth={3} />
          <input
            data-testid="depositi-search-input"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            placeholder="Cerca per cliente, telefono, targa, marca, misura..."
            className="flex-1 p-3 outline-none font-medium"
          />
          {filters.search && (
            <button onClick={() => setFilter("search", "")} className="p-2 mr-1 hover:text-[#E60000]" data-testid="clear-search">
              <X className="w-4 h-4" strokeWidth={3} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select label="Stato" value={filters.stato} onChange={(v) => setFilter("stato", v)} options={STATI} testid="filter-stato" />
          <Select label="Magazzino" value={filters.magazzino} onChange={(v) => setFilter("magazzino", v)} options={MAGAZZINI} testid="filter-magazzino" />
          <Select label="Stagionalità" value={filters.stagionalita} onChange={(v) => setFilter("stagionalita", v)} options={STAGIONI} testid="filter-stagionalita" />
          <Select label="Scaffale" value={filters.scaffale} onChange={(v) => setFilter("scaffale", v)} options={SCAFFALI} testid="filter-scaffale" />
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">Settore</label>
            <input
              data-testid="filter-settore"
              type="number"
              min="1" max="121"
              value={filters.settore}
              onChange={(e) => setFilter("settore", e.target.value)}
              placeholder="1-121"
              className="w-full border-2 border-black p-2.5 outline-none focus:border-[#E60000] font-mono-data text-sm"
            />
          </div>
        </div>

        {activeFilterCount > 0 && (
          <button
            data-testid="clear-filters"
            onClick={clearFilters}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-[#E60000]"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} /> Pulisci filtri ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Results */}
      <div className="space-y-3">
        <div className="text-xs font-bold uppercase tracking-widest">
          {loading ? "Caricamento..." : `${deposits.length} risultati`}
        </div>

        {!loading && deposits.length === 0 && (
          <div className="border-2 border-black p-8 text-center text-sm font-bold uppercase">
            Nessun deposito trovato
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deposits.map((d) => (
            <div
              key={d.id}
              data-testid={`deposit-card-${d.id}`}
              className="border-2 border-black bg-white p-5 brutal-shadow brutal-shadow-hover"
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#E60000]">
                    {d.tipo_intervento} • {d.data_intervento}
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] mt-1">
                    Scheda: {d.numero_scheda || "—"} • Data lavorazione: {d.data_lavorazione || "—"}
                  </div>
                  <div className="font-display font-black text-xl uppercase mt-0.5">{d.cliente_nome}</div>
                  {d.cliente_telefono && (
                    <div className="font-mono-data text-xs mt-0.5">{d.cliente_telefono}</div>
                  )}
                </div>
                <StatusBadge stato={d.stato} />
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-3">
                <EuropeanLicensePlate plate={d.targa} size="md" />
                <div className="text-sm font-bold">
                  {d.marca_veicolo} {d.modello_veicolo}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs border-t-2 border-black pt-3">
                <Info label="Pneumatico" value={`${d.marca_pneumatico} ${d.modello_pneumatico || ""}`.trim()} />
                <Info label="Misura" value={d.misura} mono />
                <Info label="Stagione" value={d.stagionalita} />
                <Info label="Quantità" value={`${d.quantita} pz`} />
                <Info label="Magazzino" value={d.magazzino} accent />
                <Info label="Posizione" value={`${d.settore}${d.scaffale}${d.posizione ? "•"+d.posizione : ""}`} mono accent />
                <Info label="Scheda smaltimento (ss)" value={d.numero_scheda_smaltimento} mono />
                <Info label="Scheda riconsegna (sr)" value={d.numero_scheda_riconsegna} mono />
              </div>

              {/* Flag badges */}
              {(d.con_cerchio || d.fuori_tempo_limite) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {d.con_cerchio && (
                    <span className="inline-flex items-center border-2 border-black bg-black text-white px-2 py-1 text-[10px] font-black uppercase tracking-widest">
                      Con Cerchione
                    </span>
                  )}
                  {d.fuori_tempo_limite && (
                    <span className="inline-flex items-center border-2 border-black bg-[#E60000] text-white px-2 py-1 text-[10px] font-black uppercase tracking-widest">
                      FTM • Fuori Tempo Limite
                    </span>
                  )}
                </div>
              )}

              {d.note && (
                <div className="mt-3 text-xs border-t-2 border-black pt-2 italic">
                  {d.note}
                </div>
              )}

              <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t-2 border-black">
                <div className="flex gap-1">
                  {STATI.map((s) => (
                    <button
                      key={s}
                      data-testid={`change-stato-${d.id}-${s}`}
                      onClick={() => changeStato(d, s)}
                      disabled={d.stato === s}
                      className={`text-[10px] font-black uppercase tracking-widest border-2 border-black px-2 py-1 ${
                        d.stato === s ? "bg-black text-white" : "bg-white hover:bg-[#E60000] hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1">
                  <button
                    data-testid={`whatsapp-${d.id}`}
                    onClick={() => sendWhatsApp(d)}
                    title="Invia promemoria WhatsApp"
                    className="p-2 border-2 border-black bg-black text-[#E60000] hover:bg-[#E60000] hover:text-white"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={3} />
                  </button>
                  <button
                    data-testid={`scheda-${d.id}`}
                    onClick={() => navigate(`/depositi/${d.id}/scheda`)}
                    title="Stampa scheda smaltimento/riconsegna (PDF)"
                    className="p-2 border-2 border-black bg-white text-black hover:bg-black hover:text-white"
                  >
                    <FileText className="w-4 h-4" strokeWidth={3} />
                  </button>
                  <button
                    data-testid={`edit-deposit-${d.id}`}
                    onClick={() => navigate(`/depositi/${d.id}`)}
                    className="p-2 border-2 border-black hover:bg-black hover:text-white"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={3} />
                  </button>
                  <button
                    data-testid={`delete-deposit-${d.id}`}
                    onClick={() => remove(d)}
                    className="p-2 border-2 border-black bg-[#E60000] text-white hover:bg-black"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={3} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border-2 border-black p-2.5 outline-none focus:border-[#E60000] bg-white font-bold text-sm"
    >
      <option value="">Tutti</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const Info = ({ label, value, mono, accent }) => (
  <div>
    <div className="text-[9px] font-black uppercase tracking-widest opacity-60">{label}</div>
    <div className={`${mono ? "font-mono-data" : ""} ${accent ? "text-[#E60000]" : ""} font-bold text-sm`}>
      {value || "—"}
    </div>
  </div>
);
